import React, { useState, useEffect, useCallback } from "react";
import {
  Bot, Save, AlertCircle, Loader2, BarChart3, Hand, Plus, X, Send,
  Trash2, Inbox, FlaskConical, Zap, Settings, BookOpen, Clock, MessageSquare, ShieldAlert,
} from "lucide-react";
import { Form } from "react-bootstrap";
import {
  AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent,
  StatsCard, StatsGrid, AppBadge, EmptyState, Separator, Tabs, TabsList, TabsTrigger, TabsContent,
} from "../ui";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import AIAuditLogCard from "./AIAuditLogCard";

// ─── Currency helpers ──────────────────────────────────────────────────────
// OpenAI bills in USD. We display every monetary amount in INR for Indian
// institutes. The fixed conversion rate is intentional (no live FX) — admins
// only need a rough idea of spend; exact billing is handled by OpenAI directly.
const USD_TO_INR = 84;
const fmtINR = (usd, decimals = 2) => {
  const n = Number(usd) * USD_TO_INR;
  if (!Number.isFinite(n)) return "₹0";
  if (decimals === 0 || n >= 100) return "₹" + Math.round(n).toLocaleString("en-IN");
  return "₹" + n.toFixed(decimals);
};
const inrToUsd = (inr) => Number(inr) / USD_TO_INR;

const DAYS = [
  { value: 0, label: "Sun" }, { value: 1, label: "Mon" }, { value: 2, label: "Tue" },
  { value: 3, label: "Wed" }, { value: 4, label: "Thu" }, { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const LANGUAGES = [
  { code: "en", label: "English" }, { code: "hi", label: "Hindi" },
  { code: "ta", label: "Tamil" }, { code: "te", label: "Telugu" },
  { code: "kn", label: "Kannada" }, { code: "ml", label: "Malayalam" },
  { code: "mr", label: "Marathi" }, { code: "bn", label: "Bengali" },
  { code: "gu", label: "Gujarati" }, { code: "pa", label: "Punjabi" },
  { code: "ur", label: "Urdu" }, { code: "ar", label: "Arabic" },
  { code: "es", label: "Spanish" }, { code: "fr", label: "French" },
];

// ─── Reusable styles using CSS variables (theme-aware) ────────────────────
const cardSurface = {
  background: "var(--bg-secondary, #fafafa)",
  border: "1px solid var(--border-primary, #eee)",
  borderRadius: 6,
  padding: 12,
};
const mutedText = { color: "var(--text-secondary, #666)" };
const subtleText = { color: "var(--text-tertiary, #888)", fontSize: 12 };
const kbdStyle = {
  padding: "1px 4px",
  border: "1px solid var(--border-primary, #ccc)",
  borderRadius: 3,
  background: "var(--bg-tertiary, #f7f7f7)",
  color: "var(--text-secondary, #555)",
  fontSize: 11,
};

const AIAgentTab = ({ showToast }) => {
  const canReply = can("whatsapp.reply");
  const canManage = can("whatsapp.manage");
  const [config, setConfig] = useState(null);
  const [usage, setUsage] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [confHist, setConfHist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [planLocked, setPlanLocked] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [excludeInput, setExcludeInput] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [snippetInput, setSnippetInput] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [draftEdits, setDraftEdits] = useState({});
  const [draftBusy, setDraftBusy] = useState({});
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testRunning, setTestRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data: cfgRes }, { data: usageRes }] = await Promise.all([
        apiClient.get("/ai/whatsapp/config"),
        apiClient.get("/ai/whatsapp/usage"),
      ]);
      setConfig(cfgRes.config);
      setUsage(usageRes.usage);
      setPlanLocked(false);
      apiClient.get("/ai/whatsapp/usage/hourly")
        .then(({ data }) => setHourly(data))
        .catch(() => setHourly(null));
      apiClient.get("/ai/whatsapp/usage/confidence-histogram?days=30")
        .then(({ data }) => setConfHist(data))
        .catch(() => setConfHist(null));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402 || status === 403) {
        setPlanLocked(true);
        setError(err?.response?.data?.message || "Your plan does not include the AI WhatsApp Agent.");
      } else {
        setError(err?.response?.data?.message || "Failed to load AI config");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try {
      const { data } = await apiClient.get("/ai/whatsapp/drafts?status=pending&limit=100");
      setDrafts(data.drafts || []);
    } catch (_) { /* non-fatal */ }
    finally { setDraftsLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!planLocked && !loading) loadDrafts(); }, [planLocked, loading, loadDrafts]);

  // Auto-refresh hourly stats every 60s while page is open
  useEffect(() => {
    if (planLocked) return undefined;
    const t = setInterval(() => {
      apiClient.get("/ai/whatsapp/usage/hourly")
        .then(({ data }) => setHourly(data))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [planLocked]);

  const setDraftBusyFor = (id, val) => setDraftBusy((b) => ({ ...b, [id]: val }));

  const sendDraft = async (d) => {
    setDraftBusyFor(d._id, "send");
    try {
      const editedText = draftEdits[d._id];
      const body = editedText && editedText.trim() && editedText.trim() !== d.draftText
        ? { editedText: editedText.trim() } : {};
      await apiClient.post(`/ai/whatsapp/drafts/${d._id}/send`, body);
      showToast("Reply sent", "success");
      setDrafts((arr) => arr.filter((x) => x._id !== d._id));
    } catch (err) {
      showToast(err?.response?.data?.message || "Send failed", "error");
    } finally { setDraftBusyFor(d._id, null); }
  };

  const discardDraft = async (d) => {
    setDraftBusyFor(d._id, "discard");
    try {
      await apiClient.post(`/ai/whatsapp/drafts/${d._id}/discard`);
      setDrafts((arr) => arr.filter((x) => x._id !== d._id));
    } catch (err) {
      showToast(err?.response?.data?.message || "Discard failed", "error");
    } finally { setDraftBusyFor(d._id, null); }
  };

  const runTest = async () => {
    if (!testMsg.trim()) return;
    setTestRunning(true);
    setTestResult(null);
    try {
      const { data } = await apiClient.post("/ai/whatsapp/test", { sampleMessage: testMsg.trim() });
      setTestResult(data);
    } catch (err) {
      setTestResult({ error: err?.response?.data?.message || "Test failed" });
    } finally { setTestRunning(false); }
  };

  const update = (patch) => setConfig((c) => ({ ...c, ...patch }));
  const updateBH = (patch) => setConfig((c) => ({ ...c, businessHours: { ...c.businessHours, ...patch } }));
  const updateLF = (patch) => setConfig((c) => ({ ...c, leadFollowup: { ...c.leadFollowup, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/ai/whatsapp/config", config);
      setConfig(data.config);
      showToast("AI agent settings saved", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const addToList = (field, value, clearFn) => {
    const v = (value || "").trim();
    if (!v) return;
    const arr = config?.[field] || [];
    if (arr.includes(v)) return;
    update({ [field]: [...arr, v] });
    clearFn("");
  };
  const removeFromList = (field, idx) => {
    const arr = [...(config?.[field] || [])];
    arr.splice(idx, 1);
    update({ [field]: arr });
  };
  const toggleDay = (d) => {
    const days = config.businessHours.days || [];
    updateBH({ days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort() });
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-secondary)" }}>
        <Loader2 className="spin" size={28} /> Loading AI agent settings…
      </div>
    );
  }
  if (planLocked) {
    return (
      <EmptyState
        icon={<Bot size={32} />}
        title="AI WhatsApp Agent — Premium Feature"
        description={error || "Upgrade your plan to enable AI-powered WhatsApp replies for leads and students."}
      />
    );
  }
  if (!config) {
    return <EmptyState icon={<AlertCircle size={32} />} title="Unable to load" description={error} />;
  }

  return (
    <div className="wa-ai-agent">
      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview"><BarChart3 size={14} /> Overview</TabsTrigger>
          <TabsTrigger value="drafts">
            <Inbox size={14} /> Drafts
            {drafts.length > 0 && (
              <AppBadge variant="warning" style={{ marginLeft: 6 }}>{drafts.length}</AppBadge>
            )}
          </TabsTrigger>
          <TabsTrigger value="behavior"><Settings size={14} /> Behavior</TabsTrigger>
          <TabsTrigger value="knowledge"><BookOpen size={14} /> Knowledge</TabsTrigger>
          <TabsTrigger value="hours"><Clock size={14} /> Hours & Limits</TabsTrigger>
          <TabsTrigger value="followup"><MessageSquare size={14} /> Follow-up</TabsTrigger>
          <TabsTrigger value="audit"><ShieldAlert size={14} /> Audit</TabsTrigger>
        </TabsList>

        {/* ─── OVERVIEW TAB ────────────────────────────────────────────── */}
        <TabsContent value="overview">
          <OverviewPanel
            usage={usage}
            hourly={hourly}
            config={config}
            confHist={confHist}
          />
        </TabsContent>

        {/* ─── DRAFTS & TEST TAB ───────────────────────────────────────── */}
        <TabsContent value="drafts">
          <DraftsPanel
            config={config}
            drafts={drafts}
            draftsLoading={draftsLoading}
            draftEdits={draftEdits}
            setDraftEdits={setDraftEdits}
            draftBusy={draftBusy}
            sendDraft={sendDraft}
            discardDraft={discardDraft}
            loadDrafts={loadDrafts}
            testOpen={testOpen}
            setTestOpen={setTestOpen}
            testMsg={testMsg}
            setTestMsg={setTestMsg}
            testResult={testResult}
            setTestResult={setTestResult}
            testRunning={testRunning}
            runTest={runTest}
            canReply={canReply}
            canManage={canManage}
          />
        </TabsContent>

        {/* ─── BEHAVIOR TAB (mode + escalation) ────────────────────────── */}
        <TabsContent value="behavior">
          <BehaviorPanel
            config={config}
            update={update}
            keywordInput={keywordInput}
            setKeywordInput={setKeywordInput}
            addToList={addToList}
            removeFromList={removeFromList}
            excludeInput={excludeInput}
            setExcludeInput={setExcludeInput}
          />
        </TabsContent>

        {/* ─── KNOWLEDGE & PERSONA TAB ─────────────────────────────────── */}
        <TabsContent value="knowledge">
          <KnowledgePanel
            config={config}
            update={update}
            snippetInput={snippetInput}
            setSnippetInput={setSnippetInput}
            addToList={addToList}
            removeFromList={removeFromList}
          />
        </TabsContent>

        {/* ─── BUSINESS HOURS & SPEND CAPS TAB ─────────────────────────── */}
        <TabsContent value="hours">
          <HoursAndCapsPanel
            config={config}
            update={update}
            updateBH={updateBH}
            toggleDay={toggleDay}
          />
        </TabsContent>

        {/* ─── LEAD FOLLOW-UP TAB ──────────────────────────────────────── */}
        <TabsContent value="followup">
          <FollowupPanel config={config} updateLF={updateLF} />
        </TabsContent>

        {/* ─── AUDIT LOG TAB ───────────────────────────────────────────── */}
        <TabsContent value="audit">
          <AIAuditLogCard />
        </TabsContent>
      </Tabs>

      {canManage && (
        <>
          <Separator />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <AppButton variant="secondary" onClick={load} disabled={saving}>Reset</AppButton>
            <AppButton onClick={save} disabled={saving}>
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Save
            </AppButton>
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// OVERVIEW PANEL — usage stats + live last-hour meter + confidence histogram
// ═══════════════════════════════════════════════════════════════════════════
const OverviewPanel = ({ usage, hourly, config, confHist }) => (
  <>
    <StatsGrid style={{ marginBottom: 16 }}>
      <StatsCard icon={Bot} title="Replies generated" value={usage?.repliesGenerated || 0} iconColor="violet" />
      <StatsCard icon={BarChart3} title="Replies sent" value={usage?.repliesSent || 0} iconColor="blue" />
      <StatsCard icon={Hand} title="Handoffs" value={usage?.handoffsTriggered || 0} iconColor="amber" />
      <StatsCard icon={AlertCircle} title="Total cost" value={fmtINR(usage?.totalCostUsd || 0, 0)} iconColor="emerald" />
    </StatsGrid>

    {hourly && (config.maxCostUsdPerHour > 0 || hourly.stats.totalReplies > 0) && (() => {
      const limitUsd = hourly.caps?.maxCostUsdPerHour || 0;
      const costUsd = hourly.stats?.totalCostUsd || 0;
      const pct = limitUsd > 0 ? Math.min(100, Math.round((costUsd / limitUsd) * 100)) : 0;
      const barColor = pct >= 100 ? "#dc2626" : pct >= 80 ? "#d97706" : "#059669";
      return (
        <AppCard style={{ marginBottom: 16 }}>
          <AppCardHeader>
            <AppCardTitle><Zap size={16} /> Last hour — live activity</AppCardTitle>
            <span style={{ fontSize: 11, ...mutedText }}>auto-refresh every 60s</span>
          </AppCardHeader>
          <AppCardContent>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: limitUsd > 0 ? 12 : 0 }}>
              <div><strong>{hourly.stats.totalReplies}</strong> replies</div>
              <div><strong>{fmtINR(costUsd)}</strong> spend</div>
              <div><strong>{hourly.stats.uniquePhoneCount}</strong> unique recipients</div>
              <div style={mutedText}>
                {(hourly.stats.totalPromptTokens + hourly.stats.totalCompletionTokens).toLocaleString()} tokens
              </div>
            </div>
            {limitUsd > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span>Hourly cost cap utilisation</span>
                  <span style={{ color: barColor, fontWeight: 600 }}>
                    {fmtINR(costUsd)} / {fmtINR(limitUsd, 0)} ({pct}%)
                  </span>
                </div>
                <div style={{ height: 8, background: "var(--bg-tertiary, #e5e7eb)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: barColor, transition: "width 0.3s ease" }} />
                </div>
                {pct >= 100 && (
                  <div style={{ marginTop: 8, padding: 8, background: "hsl(var(--danger-soft))", color: "hsl(var(--danger-fg))", borderRadius: 4, fontSize: 12 }}>
                    <AlertCircle size={12} /> Hourly cost cap reached — AI is handing off all new conversations until the next hour.
                  </div>
                )}
              </>
            )}
            {Array.isArray(hourly.topPhones) && hourly.topPhones.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 12 }}>
                <span style={{ ...mutedText, marginRight: 8 }}>Top recipients:</span>
                {hourly.topPhones.map((p) => {
                  const customerLimit = hourly.caps.maxRepliesPerCustomerPerHour || 5;
                  const isAtLimit = p.count >= customerLimit;
                  return (
                    <AppBadge
                      key={p.phone}
                      variant={isAtLimit ? "danger" : p.count >= customerLimit * 0.8 ? "warning" : "secondary"}
                      style={{ marginRight: 4 }}
                      title={`${fmtINR(p.costUsd)} spent in the last hour`}
                    >
                      {p.phone} ({p.count}/{customerLimit})
                    </AppBadge>
                  );
                })}
              </div>
            )}
          </AppCardContent>
        </AppCard>
      );
    })()}

    {confHist && confHist.totalCount > 0 && (() => {
      const max = Math.max(...confHist.buckets.map((b) => b.count), 1);
      return (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle><BarChart3 size={16} /> Confidence distribution — last {confHist.days} days</AppCardTitle>
            <span style={{ fontSize: 11, ...mutedText }}>{confHist.totalCount} AI replies</span>
          </AppCardHeader>
          <AppCardContent>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120, marginBottom: 12 }}>
              {confHist.buckets.map((b) => {
                const h = Math.round((b.count / max) * 100);
                const color = b.lower < 0.5 ? "#dc2626" : b.lower < 0.7 ? "#d97706" : "#059669";
                return (
                  <div
                    key={b.range}
                    style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}
                    title={`${b.range}: ${b.count} replies (avg cost ${fmtINR(b.avgCost)})`}
                  >
                    <div style={{ fontSize: 10, marginBottom: 2, ...mutedText }}>{b.count || ""}</div>
                    <div style={{
                      width: "100%", height: `${h}%`, background: color,
                      borderRadius: "3px 3px 0 0", minHeight: b.count > 0 ? 4 : 0,
                    }} />
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 6, fontSize: 10, ...mutedText }}>
              {confHist.buckets.map((b) => (
                <div key={b.range} style={{ flex: 1, textAlign: "center" }}>{b.range}</div>
              ))}
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 16, fontSize: 12, color: "var(--text-secondary)" }}>
              <span><strong style={{ color: "#dc2626" }}>{confHist.lowConfidenceCount}</strong> low confidence (&lt;0.5)</span>
              <span><strong style={{ color: "#059669" }}>{confHist.highConfidenceCount}</strong> high confidence (&ge;0.7)</span>
              {confHist.lowConfidenceCount / confHist.totalCount > 0.2 && (
                <span style={{ color: "#d97706" }}>
                  <AlertCircle size={12} /> &gt;20% low-confidence — consider expanding the knowledge base.
                </span>
              )}
            </div>
          </AppCardContent>
        </AppCard>
      );
    })()}
  </>
);

// ═══════════════════════════════════════════════════════════════════════════
// DRAFTS PANEL — pending AI replies + sample-message test playground
// ═══════════════════════════════════════════════════════════════════════════
const DraftsPanel = ({
  config, drafts, draftsLoading, draftEdits, setDraftEdits, draftBusy,
  sendDraft, discardDraft, loadDrafts,
  testOpen, setTestOpen, testMsg, setTestMsg, testResult, setTestResult,
  testRunning, runTest, canReply, canManage,
}) => (
  <AppCard>
    <AppCardHeader>
      <AppCardTitle>
        <Inbox size={16} /> Pending AI Replies — Review Queue
        {drafts.length > 0 && <AppBadge variant="warning" style={{ marginLeft: 8 }}>{drafts.length}</AppBadge>}
      </AppCardTitle>
      <div style={{ display: "flex", gap: 8 }}>
        <AppButton size="sm" variant="outline" onClick={loadDrafts} disabled={draftsLoading}>
          {draftsLoading ? <Loader2 size={14} className="spin" /> : "Refresh"}
        </AppButton>
        {canManage && (
          <AppButton size="sm" variant="outline" onClick={() => { setTestOpen((o) => !o); setTestResult(null); }}>
            <FlaskConical size={14} /> {testOpen ? "Close test" : "Test reply"}
          </AppButton>
        )}
      </div>
    </AppCardHeader>
    <AppCardContent>
      {testOpen && (
        <div style={{
          marginBottom: 16, padding: 12,
          border: "1px dashed var(--border-primary)", borderRadius: 6,
          background: "var(--bg-secondary)",
        }}>
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Try a sample customer message</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={testMsg}
            onChange={(e) => setTestMsg(e.target.value)}
            placeholder="e.g., What's the fee for the React batch?"
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <AppButton size="sm" onClick={runTest} disabled={testRunning || !testMsg.trim()}>
              {testRunning ? <Loader2 size={14} className="spin" /> : <FlaskConical size={14} />} Generate preview
            </AppButton>
          </div>
          {testResult && (
            <div style={{
              marginTop: 10, padding: 10,
              background: "var(--bg-primary)",
              border: "1px solid var(--border-primary)",
              borderRadius: 4, fontSize: 13,
            }}>
              {testResult.error ? (
                <div style={{ color: "hsl(var(--danger-fg))" }}>{testResult.error}</div>
              ) : (
                <>
                  <div style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}>{testResult.replyText}</div>
                  <div style={{ fontSize: 11, ...mutedText }}>
                    intent: {testResult.intent || "—"} · confidence: {Math.round((testResult.confidence || 0) * 100)}% · {fmtINR(testResult.costUsd || 0)} · {testResult.latencyMs || 0}ms · {testResult.model || ""}
                  </div>
                  {Array.isArray(testResult.knowledgeChunks) && testResult.knowledgeChunks.length > 0 && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--border-primary)" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
                        📚 Retrieved knowledge ({testResult.knowledgeChunks.length})
                        {testResult.knowledgeChunkIds?.length > 0 && (
                          <span style={{ color: "#059669", marginLeft: 6 }}>
                            · {testResult.knowledgeChunkIds.length} cited by AI
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {testResult.knowledgeChunks.map((c, i) => {
                          const cited = (testResult.knowledgeChunkIds || []).includes(c._id);
                          return (
                            <div
                              key={c._id}
                              style={{
                                padding: 6,
                                background: cited ? "hsl(var(--success-soft))" : "var(--bg-secondary)",
                                border: `1px solid ${cited ? "hsl(var(--success) / 0.35)" : "var(--border-primary)"}`,
                                borderRadius: 3, fontSize: 11,
                              }}
                            >
                              <div style={{ fontWeight: 500 }}>
                                [{i + 1}] {c.title || "(untitled)"}{" "}
                                <AppBadge variant="secondary">{c.source}</AppBadge>
                                <span style={{ marginLeft: 6, ...mutedText }}>score: {(c.score || 0).toFixed(3)}</span>
                                {cited && <AppBadge variant="success" style={{ marginLeft: 6 }}>cited</AppBadge>}
                              </div>
                              <div style={{ color: "var(--text-secondary)", marginTop: 2 }}>
                                {c.preview}{c.preview?.length >= 200 ? "…" : ""}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {Array.isArray(testResult.toolsUsed) && testResult.toolsUsed.length > 0 && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--border-primary)" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>🛠 Tools called ({testResult.toolsUsed.length})</div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {testResult.toolsUsed.map((t, i) => (
                          <AppBadge key={i} variant={t.ok ? "secondary" : "danger"} title={`${t.durationMs}ms`}>
                            {t.name} {t.ok ? "✓" : "✗"} ({t.durationMs}ms)
                          </AppBadge>
                        ))}
                      </div>
                    </div>
                  )}
                  {testResult.handoff && (
                    <div style={{
                      marginTop: 8, padding: 6,
                      background: "hsl(var(--danger-soft))",
                      color: "hsl(var(--danger-fg))",
                      borderRadius: 3, fontSize: 11,
                    }}>
                      <AlertCircle size={11} /> Handoff triggered: {testResult.handoffReason || "—"}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
      {drafts.length === 0 ? (
        <EmptyState
          icon={<Inbox size={32} />}
          title="No pending drafts"
          description={config.mode === "suggest"
            ? "Drafts will appear here when AI suggests replies."
            : "AI is in auto/off mode — drafts only created in Suggest mode."}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {drafts.map((d) => {
            const text = draftEdits[d._id] !== undefined ? draftEdits[d._id] : d.draftText;
            const busy = draftBusy[d._id];
            return (
              <div key={d._id} style={{ border: "1px solid var(--border-primary)", borderRadius: 6, padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, fontSize: 12 }}>
                  <div>
                    <strong>{d.customerPhone}</strong>
                    {d.aiMeta?.intent && <AppBadge variant="secondary" style={{ marginLeft: 8 }}>{d.aiMeta.intent}</AppBadge>}
                    {typeof d.aiMeta?.confidence === "number" && (
                      <span style={{ marginLeft: 8, ...mutedText }}>
                        {Math.round(d.aiMeta.confidence * 100)}% conf
                      </span>
                    )}
                  </div>
                  <span style={mutedText}>{new Date(d.createdAt).toLocaleString()}</span>
                </div>
                <Form.Control
                  as="textarea" rows={3}
                  value={text}
                  onChange={(e) => setDraftEdits((m) => ({ ...m, [d._id]: e.target.value }))}
                  readOnly={!canReply}
                  onKeyDown={(e) => {
                    if (!canReply) return;
                    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                      e.preventDefault();
                      if (!busy && text.trim()) sendDraft(d);
                    } else if ((e.ctrlKey || e.metaKey) && (e.key === "Backspace" || e.key === "Delete")) {
                      e.preventDefault();
                      if (!busy) discardDraft(d);
                    }
                  }}
                  style={{ fontSize: 13 }}
                />
                {canReply && (
                  <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <AppButton size="sm" onClick={() => sendDraft(d)} disabled={!!busy || !text.trim()}>
                      {busy === "send" ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Send
                    </AppButton>
                    <AppButton size="sm" variant="outline" onClick={() => discardDraft(d)} disabled={!!busy}>
                      {busy === "discard" ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Discard
                    </AppButton>
                    <span style={{ marginLeft: "auto", ...subtleText }}>
                      <kbd style={kbdStyle}>Ctrl/⌘+Enter</kbd> send · <kbd style={kbdStyle}>Ctrl/⌘+⌫</kbd> discard
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AppCardContent>
  </AppCard>
);

// ═══════════════════════════════════════════════════════════════════════════
// BEHAVIOR PANEL — mode + lead/student toggles + escalation
// ═══════════════════════════════════════════════════════════════════════════
const BehaviorPanel = ({
  config, update, keywordInput, setKeywordInput,
  addToList, removeFromList, excludeInput, setExcludeInput,
}) => (
  <>
    <AppCard>
      <AppCardHeader><AppCardTitle><Bot size={16} /> Mode &amp; Activation</AppCardTitle></AppCardHeader>
      <AppCardContent>
        <Form.Check
          type="switch" id="ai-enabled"
          label="Enable AI WhatsApp Agent"
          checked={!!config.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
        <div style={{ marginTop: 12 }}>
          <Form.Label>Mode</Form.Label>
          <Form.Select value={config.mode} onChange={(e) => update({ mode: e.target.value })}>
            <option value="off">Off — do not generate replies</option>
            <option value="suggest">Suggest — draft replies for staff to review</option>
            <option value="auto">Auto — send replies automatically (use with care)</option>
          </Form.Select>
          <Form.Text muted>
            We recommend starting in <strong>Suggest</strong> and switching to Auto only after reviewing draft quality.
          </Form.Text>
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap" }}>
          <Form.Check
            type="switch" id="ai-leads"
            label="Respond to leads (unknown contacts)"
            checked={!!config.respondToLeads}
            onChange={(e) => update({ respondToLeads: e.target.checked })}
          />
          <Form.Check
            type="switch" id="ai-students"
            label="Respond to enrolled students"
            checked={!!config.respondToStudents}
            onChange={(e) => update({ respondToStudents: e.target.checked })}
          />
          <Form.Check
            type="switch" id="ai-consent"
            label="Require consent (opt-in) before replying to students"
            checked={!!config.consentRequired}
            onChange={(e) => update({ consentRequired: e.target.checked })}
          />
        </div>
      </AppCardContent>
    </AppCard>

    <AppCard style={{ marginTop: 16 }}>
      <AppCardHeader><AppCardTitle>Escalation &amp; Handoff</AppCardTitle></AppCardHeader>
      <AppCardContent>
        <Form.Group>
          <Form.Label>Escalation keywords (trigger immediate handoff)</Form.Label>
          <div style={{ display: "flex", gap: 8 }}>
            <Form.Control
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("escalationKeywords", keywordInput, setKeywordInput))}
              placeholder="e.g. refund, complaint, lawyer"
            />
            <AppButton variant="secondary" onClick={() => addToList("escalationKeywords", keywordInput, setKeywordInput)}>
              <Plus size={14} /> Add
            </AppButton>
          </div>
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(config.escalationKeywords || []).map((k, i) => (
              <AppBadge key={i} variant="warning" onClick={() => removeFromList("escalationKeywords", i)} style={{ cursor: "pointer" }}>
                {k} <X size={10} />
              </AppBadge>
            ))}
          </div>
        </Form.Group>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginTop: 12 }}>
          <Form.Group>
            <Form.Label>Escalate after N AI turns</Form.Label>
            <Form.Control
              type="number" min={1} max={50}
              value={config.escalateAfterTurns}
              onChange={(e) => update({ escalateAfterTurns: Number(e.target.value) })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Handoff message (sent to user before pausing AI)</Form.Label>
            <Form.Control
              value={config.handoffMessage || ""}
              onChange={(e) => update({ handoffMessage: e.target.value })}
            />
          </Form.Group>
        </div>
      </AppCardContent>
    </AppCard>

    <AppCard style={{ marginTop: 16 }}>
      <AppCardHeader><AppCardTitle>Excluded phone numbers</AppCardTitle></AppCardHeader>
      <AppCardContent>
        <div style={{ display: "flex", gap: 8 }}>
          <Form.Control
            value={excludeInput}
            onChange={(e) => setExcludeInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("excludePhones", excludeInput, setExcludeInput))}
            placeholder="e.g. 9876543210 (last digits suffix match)"
          />
          <AppButton variant="secondary" onClick={() => addToList("excludePhones", excludeInput, setExcludeInput)}>
            <Plus size={14} /> Add
          </AppButton>
        </div>
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(config.excludePhones || []).map((p, i) => (
            <AppBadge key={i} variant="secondary" onClick={() => removeFromList("excludePhones", i)} style={{ cursor: "pointer" }}>
              {p} <X size={10} />
            </AppBadge>
          ))}
        </div>
      </AppCardContent>
    </AppCard>
  </>
);

// ═══════════════════════════════════════════════════════════════════════════
// KNOWLEDGE PANEL — persona, snippets, languages, vision
// ═══════════════════════════════════════════════════════════════════════════
const KnowledgePanel = ({
  config, update, snippetInput, setSnippetInput, addToList, removeFromList,
}) => (
  <AppCard>
    <AppCardHeader><AppCardTitle><BookOpen size={16} /> Persona &amp; Knowledge</AppCardTitle></AppCardHeader>
    <AppCardContent>
      <div style={{
        ...cardSurface,
        marginBottom: 12,
        background: "hsl(var(--info-soft))",
        borderColor: "hsl(var(--info) / 0.35)",
        fontSize: 12,
        color: "var(--text-secondary)",
      }}>
        💡 The AI also pulls answers from your courses & upcoming batches automatically.
        Add the <strong>Knowledge Base</strong> tab to seed and manage that catalog. Internal
        details (instructor names, staff info, host links) are <strong>never</strong> shared with customers.
      </div>

      <Form.Group>
        <Form.Label>Assistant persona / system prompt</Form.Label>
        <Form.Control
          as="textarea" rows={3}
          value={config.persona || ""}
          onChange={(e) => update({ persona: e.target.value })}
          placeholder="You are a helpful, polite assistant for our institute. Keep replies short and friendly."
        />
      </Form.Group>
      <Form.Group style={{ marginTop: 12 }}>
        <Form.Label>Custom instructions (do/don't, tone, signature)</Form.Label>
        <Form.Control
          as="textarea" rows={3}
          value={config.customInstructions || ""}
          onChange={(e) => update({ customInstructions: e.target.value })}
          placeholder="Always end with our website URL. Never share instructor contact details."
        />
      </Form.Group>

      <Form.Group style={{ marginTop: 12 }}>
        <Form.Label>Knowledge snippets (FAQs, course info, policies)</Form.Label>
        <div style={{ display: "flex", gap: 8 }}>
          <Form.Control
            as="textarea" rows={2}
            value={snippetInput}
            onChange={(e) => setSnippetInput(e.target.value)}
            placeholder="e.g. We are open Mon-Sat 9-8. Demo classes are free…"
          />
          <AppButton variant="secondary" onClick={() => addToList("knowledgeSnippets", snippetInput, setSnippetInput)}>
            <Plus size={14} /> Add
          </AppButton>
        </div>
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          {(config.knowledgeSnippets || []).map((s, i) => (
            <div key={i} style={{
              background: "var(--bg-secondary, #f4f6f8)",
              border: "1px solid var(--border-primary)",
              padding: "6px 10px", borderRadius: 6,
              display: "flex", justifyContent: "space-between", gap: 8,
            }}>
              <span style={{ flex: 1, fontSize: 13 }}>{s}</span>
              <button type="button" className="btn-icon" onClick={() => removeFromList("knowledgeSnippets", i)}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </Form.Group>

      <Form.Group style={{ marginTop: 12 }}>
        <Form.Label>Languages the AI may reply in</Form.Label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {LANGUAGES.map((l) => {
            const checked = (config.languages || []).includes(l.code);
            return (
              <label
                key={l.code}
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  padding: "4px 8px",
                  border: `1px solid ${checked ? "var(--accent-primary, #2563eb)" : "var(--border-primary)"}`,
                  borderRadius: 4, cursor: "pointer", fontSize: 12,
                  background: checked ? "hsl(var(--info-soft))" : "var(--bg-primary)",
                  color: checked ? "var(--accent-primary, #2563eb)" : "var(--text-primary)",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const cur = config.languages || [];
                    if (e.target.checked) update({ languages: [...new Set([...cur, l.code])] });
                    else update({ languages: cur.filter((c) => c !== l.code) });
                  }}
                  style={{ margin: 0 }}
                />
                {l.label}
              </label>
            );
          })}
        </div>
        <Form.Text muted>The AI will mirror the customer's language if it's enabled here; otherwise it falls back to the first selected language.</Form.Text>
      </Form.Group>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
        <Form.Group>
          <Form.Label>Max reply length (chars)</Form.Label>
          <Form.Control
            type="number" min={50} max={2000}
            value={config.maxReplyLength}
            onChange={(e) => update({ maxReplyLength: Number(e.target.value) })}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Confidence threshold (0–1)</Form.Label>
          <Form.Control
            type="number" min={0} max={1} step={0.05}
            value={config.confidenceThreshold}
            onChange={(e) => update({ confidenceThreshold: Number(e.target.value) })}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Disclosure prefix</Form.Label>
          <Form.Control
            value={config.disclosurePrefix || ""}
            onChange={(e) => update({ disclosurePrefix: e.target.value })}
            placeholder="🤖 (AI assistant) "
          />
        </Form.Group>
      </div>

      <div style={{ ...cardSurface, marginTop: 16 }}>
        <Form.Check
          type="switch" id="ai-vision"
          label="Process inbound images (vision) — extra cost per image"
          checked={!!config.visionEnabled}
          onChange={(e) => update({ visionEnabled: e.target.checked })}
        />
        <Form.Text muted>
          When enabled, the AI can read screenshots/photos customers send (e.g., a fee receipt or course brochure).
          Uses GPT-4o-mini vision; about {fmtINR(0.001)}–{fmtINR(0.005)} per image.
        </Form.Text>
        {config.visionEnabled && (
          <Form.Group style={{ marginTop: 8, maxWidth: 280 }}>
            <Form.Label style={{ fontSize: 12 }}>Skip images larger than (bytes)</Form.Label>
            <Form.Control
              type="number"
              min={102400}
              max={20 * 1024 * 1024}
              step={102400}
              value={config.visionMaxImageBytes || 5 * 1024 * 1024}
              onChange={(e) => update({ visionMaxImageBytes: Number(e.target.value) })}
            />
            <Form.Text muted>Default 5 MB. Larger images are silently skipped.</Form.Text>
          </Form.Group>
        )}
      </div>
    </AppCardContent>
  </AppCard>
);

// ═══════════════════════════════════════════════════════════════════════════
// HOURS & CAPS PANEL — business hours + spend caps
// ═══════════════════════════════════════════════════════════════════════════
const HoursAndCapsPanel = ({ config, update, updateBH, toggleDay }) => {
  // Backend stores caps in USD; we surface them in INR. Convert on edit.
  const capInr = config.maxCostUsdPerHour ? config.maxCostUsdPerHour * USD_TO_INR : 0;
  return (
    <>
      <AppCard>
        <AppCardHeader><AppCardTitle><Clock size={16} /> Business Hours</AppCardTitle></AppCardHeader>
        <AppCardContent>
          <Form.Check
            type="switch" id="ai-bh"
            label="Only respond during business hours"
            checked={!!config.businessHoursOnly}
            onChange={(e) => update({ businessHoursOnly: e.target.checked })}
          />
          {config.businessHoursOnly && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Form.Group>
                  <Form.Label>Timezone</Form.Label>
                  <Form.Control
                    value={config.businessHours.timezone}
                    onChange={(e) => updateBH({ timezone: e.target.value })}
                    placeholder="Asia/Kolkata"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Start (HH:MM)</Form.Label>
                  <Form.Control type="time" value={config.businessHours.start} onChange={(e) => updateBH({ start: e.target.value })} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>End (HH:MM)</Form.Label>
                  <Form.Control type="time" value={config.businessHours.end} onChange={(e) => updateBH({ end: e.target.value })} />
                </Form.Group>
              </div>
              <div style={{ marginTop: 12 }}>
                <Form.Label>Days</Form.Label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {DAYS.map((d) => {
                    const active = config.businessHours.days?.includes(d.value);
                    return (
                      <button
                        type="button"
                        key={d.value}
                        onClick={() => toggleDay(d.value)}
                        style={{
                          padding: "6px 12px", borderRadius: 16,
                          border: `1px solid ${active ? "var(--accent-primary, #0d6efd)" : "var(--border-primary, #ccc)"}`,
                          background: active ? "var(--accent-primary, #0d6efd)" : "var(--bg-primary, #fff)",
                          color: active ? "#fff" : "var(--text-primary, #333)",
                          cursor: "pointer", fontSize: 13,
                        }}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader><AppCardTitle>Spend &amp; Reply Caps</AppCardTitle></AppCardHeader>
        <AppCardContent>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Group>
              <Form.Label>Max AI replies to same number / hour</Form.Label>
              <Form.Control
                type="number" min={1} max={60}
                value={config.maxRepliesPerCustomerPerHour ?? 5}
                onChange={(e) => update({ maxRepliesPerCustomerPerHour: Number(e.target.value) })}
              />
              <Form.Text muted>Anti-spam guard for noisy numbers (default 5).</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Hourly OpenAI spend cap (₹)</Form.Label>
              <Form.Control
                type="number" min={0} max={100000} step={10}
                value={Math.round(capInr)}
                onChange={(e) => update({ maxCostUsdPerHour: inrToUsd(Number(e.target.value)) })}
              />
              <Form.Text muted>
                0 disables. When exceeded, AI hands off all conversations until the next hour.
                {" "}Stored as USD ({(config.maxCostUsdPerHour || 0).toFixed(2)} USD) using a fixed
                rate of ₹{USD_TO_INR}/$1 — actual OpenAI billing may vary.
              </Form.Text>
            </Form.Group>
          </div>
        </AppCardContent>
      </AppCard>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// FOLLOW-UP PANEL — drip campaigns for stale leads
// ═══════════════════════════════════════════════════════════════════════════
const FollowupPanel = ({ config, updateLF }) => (
  <AppCard>
    <AppCardHeader><AppCardTitle><MessageSquare size={16} /> Lead Follow-Up (drip)</AppCardTitle></AppCardHeader>
    <AppCardContent>
      <Form.Check
        type="switch" id="ai-lf"
        label="Auto-follow-up on stale leads"
        checked={!!config.leadFollowup?.enabled}
        onChange={(e) => updateLF({ enabled: e.target.checked })}
      />
      {config.leadFollowup?.enabled && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginTop: 12 }}>
            <Form.Group>
              <Form.Label>Max attempts</Form.Label>
              <Form.Control
                type="number" min={1} max={10}
                value={config.leadFollowup.maxAttempts}
                onChange={(e) => updateLF({ maxAttempts: Number(e.target.value) })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Intervals (hours, comma-separated)</Form.Label>
              <Form.Control
                value={(config.leadFollowup.intervalsHours || []).join(", ")}
                onChange={(e) => updateLF({
                  intervalsHours: e.target.value.split(",").map((n) => Number(n.trim())).filter((n) => n > 0),
                })}
              />
            </Form.Group>
          </div>
          <div style={{ ...cardSurface, marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Per-attempt templates (optional)</strong>
              <AppButton size="sm" variant="outline" onClick={() => {
                const tpls = config.leadFollowup.templates || [];
                const nextAttempt = tpls.length > 0 ? Math.max(...tpls.map((t) => t.attempt)) + 1 : 1;
                updateLF({ templates: [...tpls, { attempt: nextAttempt, templateName: "", languageCode: "en_US", paramFields: [] }] });
              }}>
                <Plus size={12} /> Add template
              </AppButton>
            </div>
            <Form.Text muted style={{ display: "block", marginBottom: 8 }}>
              When set, follow-up #N will send the matching pre-approved Meta template (cheaper, deliverable beyond 24h). Otherwise the AI generates a custom message.
            </Form.Text>
            {(config.leadFollowup.templates || []).map((t, idx) => (
              <div key={idx} style={{ display: "grid", gridTemplateColumns: "60px 1fr 100px 1fr 30px", gap: 6, marginBottom: 6, alignItems: "center" }}>
                <Form.Control
                  size="sm" type="number" min={1} max={10}
                  value={t.attempt}
                  onChange={(e) => {
                    const next = [...config.leadFollowup.templates];
                    next[idx] = { ...t, attempt: Number(e.target.value) };
                    updateLF({ templates: next });
                  }}
                  title="Attempt #"
                />
                <Form.Control
                  size="sm" value={t.templateName}
                  placeholder="approved_template_name"
                  onChange={(e) => {
                    const next = [...config.leadFollowup.templates];
                    next[idx] = { ...t, templateName: e.target.value };
                    updateLF({ templates: next });
                  }}
                />
                <Form.Control
                  size="sm" value={t.languageCode || "en_US"}
                  placeholder="en_US"
                  onChange={(e) => {
                    const next = [...config.leadFollowup.templates];
                    next[idx] = { ...t, languageCode: e.target.value };
                    updateLF({ templates: next });
                  }}
                />
                <Form.Control
                  size="sm"
                  value={(t.paramFields || []).join(", ")}
                  placeholder="student.name, enquiry.course.title"
                  onChange={(e) => {
                    const next = [...config.leadFollowup.templates];
                    next[idx] = { ...t, paramFields: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) };
                    updateLF({ templates: next });
                  }}
                  title="Comma-separated profile field paths for {{1}}, {{2}}, ..."
                />
                <AppButton size="xs" variant="ghost" onClick={() => {
                  updateLF({ templates: config.leadFollowup.templates.filter((_, i) => i !== idx) });
                }}>
                  <X size={12} />
                </AppButton>
              </div>
            ))}
          </div>
        </>
      )}
    </AppCardContent>
  </AppCard>
);

export default AIAgentTab;
